import { useCallback, useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';

import { FormProvider, useNotificationContext } from 'core/states';
import { useElementTemplateActions } from 'elements/hooks';
import { IElementTemplate } from 'elements/types';

import { upsertElementTemplateSchema } from './schema';
import { IUpsertElementTemplateFormData } from './types';
import { UpsertElementTemplateFormFields } from './UpsertElementTemplateFormFields';

interface IProps {
  elementTemplate?: IElementTemplate;
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess?: (elementTemplate: IElementTemplate) => void;
}

export const UpsertElementTemplate = ({
  elementTemplate,
  isOpen,
  onClose,
  onSubmitSuccess,
}: IProps) => {
  const { clearNotification } = useNotificationContext();

  const { upsertElementTemplate } = useElementTemplateActions();

  const defaultValues = useMemo(
    () => ({
      customIdentifier: elementTemplate?.customIdentifier ?? '',
      description: elementTemplate?.description ?? '',
      id: elementTemplate?.id,
    }),
    [elementTemplate]
  );

  const handleOnClose = useCallback(
    (reset?: UseFormReset<IUpsertElementTemplateFormData>) => {
      clearNotification();
      reset?.(defaultValues);
      onClose();
    },
    [clearNotification, defaultValues, onClose]
  );

  const onSubmit = useCallback(
    (reset: UseFormReset<IUpsertElementTemplateFormData>) =>
      async (data: IUpsertElementTemplateFormData) => {
        const payload = {
          elementTemplate: {
            customIdentifier: data.customIdentifier,
            description: data.description,
            ...(elementTemplate?.id && { id: elementTemplate.id }),
          },
        };

        const { success, elementTemplate: updatedElementTemplate } =
          await upsertElementTemplate(payload);

        if (!success) {
          return;
        }

        setTimeout(() => {
          handleOnClose(reset);
          onSubmitSuccess?.(updatedElementTemplate);
        }, 1000);
      },
    [elementTemplate, handleOnClose, onSubmitSuccess, upsertElementTemplate]
  );

  return (
    <FormProvider
      schema={upsertElementTemplateSchema()}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <UpsertElementTemplateFormFields
        elementTemplate={elementTemplate}
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
      />
    </FormProvider>
  );
};
